// Get API URL from runtime config or fallback to build-time env or default
const getApiBaseUrl = () => {
  // First, try to read from window.__ENV__ (injected at runtime)
  if (window.__ENV__ && window.__ENV__.REACT_APP_API_URL) {
    return window.__ENV__.REACT_APP_API_URL;
  }
  // Second, try to read from a config.js file (loaded in index.html)
  if (window.APP_CONFIG && window.APP_CONFIG.REACT_APP_API_URL) {
    return window.APP_CONFIG.REACT_APP_API_URL;
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:5050';
};

const API_BASE_URL = getApiBaseUrl();

// Get auth token
const getToken = () => {
  return localStorage.getItem('token');
};

// Email Configuration API
export const emailConfigService = {
  // Get all email configurations
  getAllConfigurations: async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email configurations');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get email configuration by ID
  getConfigurationById: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Create new email configuration
  createConfiguration: async (configData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(configData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to create email configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update email configuration
  updateConfiguration: async (id, configData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(configData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to update email configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Delete email configuration
  deleteConfiguration: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to delete email configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Test email configuration (existing)
  testConfiguration: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations/${id}/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to test email configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Test new email configuration (before saving)
  testNewConfiguration: async (configData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(configData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to test email configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Set default configuration
  setDefaultConfiguration: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-configurations/${id}/set-default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to set default configuration');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// Email Logs API
export const emailLogsService = {
  // Get email logs with filters
  getEmailLogs: async (filters = {}) => {
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sender) queryParams.append('sender', filters.sender);
      if (filters.receiver) queryParams.append('receiver', filters.receiver);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.configurationId) queryParams.append('configurationId', filters.configurationId);

      const url = `${API_BASE_URL}/api/email-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email logs');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get email log by ID
  getEmailLogById: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-logs/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email log');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get email statistics
  getEmailLogStats: async (filters = {}) => {
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = `${API_BASE_URL}/api/email-logs/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email statistics');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// Send Email API
export const sendEmailService = {
  // Send email
  sendEmail: async (emailData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to send email');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Search users for autocomplete
  searchUsers: async (query) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email/search-users?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to search users');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Send email with template
  sendEmailWithTemplate: async (emailData) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email/send-with-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to send email with template');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// Email Templates API
export const emailTemplatesService = {
  // Get all email templates
  getAllTemplates: async (filters = {}) => {
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `${API_BASE_URL}/api/email-templates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email templates');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get email template by ID
  getTemplateById: async (id) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-templates/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email template');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get email template by code
  getTemplateByCode: async (code) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-templates/code/${code}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch email template');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get template preview
  getTemplatePreview: async (code, entityIds, customVariables) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-templates/${code}/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ entityIds, customVariables })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to generate template preview');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get available entities for template
  getAvailableEntities: async (code) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-templates/${code}/entities`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch available entities');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Resolve template variables
  resolveTemplateVariables: async (code, entityIds, customVariables) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/email-templates/${code}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ entityIds, customVariables })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to resolve template variables');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};
